<template>
  <div class="checklist-template-editor">
    <!-- TEMPLATE VIEW: name + item list -->
    <template v-if="view === 'template'">
      <q-list
        dense
        padding
        class="editor-scroll"
      >
        <q-item-wrapper
          label="Template Name"
          label-class="text-weight-medium"
          required
        >
          <q-input
            v-model="editObject.name"
            dense
            outlined
            clearable
            hide-bottom-space
            placeholder="Enter template name..."
            :error="!!errors['name']"
            :error-message="errors['name'] ? errors['name'][0] : null"
          />
        </q-item-wrapper>

        <q-separator spaced />

        <q-item-label class="text-weight-medium q-px-md q-pb-sm">
          Checklist Items
        </q-item-label>

        <q-list separator>
          <ChecklistItemRow
            v-for="(item, key) in editObject.items"
            :key="'item-' + key"
            :item="item"
            :index="key"
            :errors="getItemErrors(key)"
            @edit="editItem"
            @delete="onDeleteItem"
          />
        </q-list>

        <div class="q-px-md">
          <div
            v-if="editObject.items.length === 0"
            class="text-caption q-py-sm"
            :class="itemsError ? 'text-red-8' : 'text-grey-6'"
          >
            {{ itemsError || 'No items yet — a checklist needs at least one item.' }}
          </div>

          <q-btn
            flat
            dense
            no-caps
            color="blue"
            icon="mdi-plus"
            label="Add item"
            class="q-mt-sm"
            @click="addItem"
          />
        </div>
      </q-list>

      <q-separator />

      <q-toolbar class="editor-footer">
        <q-btn
          flat
          no-caps
          color="grey-8"
          label="Cancel"
          @click="$emit('cancel')"
        />
        <q-space />
        <q-btn
          color="green"
          icon-right="done"
          label="Save"
          @click="save"
        />
      </q-toolbar>
    </template>

    <!-- TASK VIEW: the stepper swaps in place of the template view, so
         adding/editing an item never opens another dialog -->
    <TaskStepper
      v-else
      ref="taskStepper"
      mode="template"
      @save="onStepperSave"
      @cancel="showTemplateView"
    />
  </div>
</template>

<script>
import ChecklistItemRow from './partials/ChecklistItemRow'
import TaskStepper from 'components/app/checklists/TaskStepper.vue'

/*
  Embeddable checklist template editor. Swaps internally between the template
  view (name + items) and the task stepper, so hosts (the Checklist Templates
  settings dialog, or the thin EditDialog wrapper) never stack dialogs.

  API: call reset(template) when showing. Emits:
  - saved(payload): template was persisted
  - cancel: user backed out of the editor
  - title-change(title): suggested host dialog title for the current view
*/
export default {
  components: {
    ChecklistItemRow,
    TaskStepper
  },
  emits: ['saved', 'cancel', 'title-change'],
  data () {
    return {
      editObject: { name: null, items: [] },
      view: 'template',
      editingIndex: null,
      errors: {}
    }
  },
  computed: {
    mode () {
      return this.editObject && this.editObject.id ? 'edit' : 'add'
    },
    itemsError () {
      return this.errors['items'] ? this.errors['items'][0] : null
    }
  },
  methods: {
    reset (template) {
      this.errors = {}
      this.view = 'template'
      this.editingIndex = null
      this.editObject = JSON.parse(JSON.stringify(template))
      this.emitTitle()
    },
    emitTitle () {
      let title
      if (this.view === 'task') {
        title = this.editingIndex !== null ? 'Edit Checklist Item' : 'Add Checklist Item'
      }
      else {
        title = (this.mode === 'edit' ? 'Edit' : 'Add') + ' Checklist Template'
      }
      this.$emit('title-change', title)
    },
    showTemplateView () {
      this.view = 'template'
      this.editingIndex = null
      this.emitTitle()
    },
    addItem () {
      this.editingIndex = null
      this.showStepper(null)
    },
    editItem (index) {
      this.editingIndex = index
      this.showStepper(this.editObject.items[index])
    },
    showStepper (item) {
      this.view = 'task'
      this.emitTitle()
      this.$nextTick(() => {
        this.$refs.taskStepper.reset(item, this.editingIndex)
      })
    },
    onStepperSave (item, index) {
      if (index !== null && index !== undefined) {
        // Preserve the existing sort_order when editing in place
        this.editObject.items.splice(index, 1, { ...item, sort_order: this.editObject.items[index].sort_order })
      }
      else {
        this.editObject.items.push({ ...item, sort_order: this.editObject.items.length })
      }
      this.showTemplateView()
    },
    onDeleteItem (index) {
      this.editObject.items.splice(index, 1)
      // Recalculate sort_order
      this.editObject.items.forEach((item, i) => {
        item.sort_order = i
      })
    },
    getItemErrors (index) {
      const itemErrors = {}
      for (const key of Object.keys(this.errors)) {
        const prefix = `items.${index}.`
        if (key.startsWith(prefix)) {
          const field = key.replace(prefix, '')
          itemErrors[field] = this.errors[key]
        }
      }
      return itemErrors
    },
    async save () {
      this.errors = {}

      // Validate up front — a checklist template needs at least one item
      if (this.editObject.items.length === 0) {
        this.errors = { items: ['Add at least one checklist item before saving'] }
        this.$q.notify({
          type: 'negative',
          message: 'Add at least one checklist item before saving'
        })
        return
      }

      this.$loading.show()

      const payload = {
        name: this.editObject.name,
        items: this.editObject.items.map((item, index) => ({
          text: item.text,
          is_required: item.is_required,
          requires_photo: item.requires_photo || false,
          requires_invoice: item.requires_invoice || false,
          requires_quotation: item.requires_quotation || false,
          required_form_template_ids: item.required_form_template_ids || [],
          sort_order: index
        }))
      }

      let result

      if (this.mode === 'edit') {
        result = await this.$put(`v1/checklist-templates/${this.editObject.id}`, payload)
      }
      else {
        result = await this.$post('v1/checklist-templates', payload)
      }

      this.$loading.hide()

      if (result.status === 'success') {
        this.$q.notify({
          color: 'green',
          message: 'Checklist Template successfully saved'
        })
        this.$emit('saved', result.payload)
      }
      else if (result.errors) {
        this.errors = result.errors
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Fill the dialog's flex content area (the host passes the `flex` prop) so the
   item list scrolls and the footer sits as a flex sibling pinned to the bottom
   — the same 3-row structure the System Defaults dialog uses. */
.checklist-template-editor {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.editor-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.editor-footer {
  flex: none;
}
</style>
